import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal, PageHero } from "@/components/shared";
import { CATEGORIES, IMPORTANCE, categoryShort, importanceLabel, importanceClass, formatDate } from "@/lib/content";

export default function Veille() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [importance, setImportance] = useState("all");
  const [sort, setSort] = useState("recent");

  const loadArticles = () => {
    setLoading(true);
    setError(false);
    api
      .get("/articles")
      .then(({ data }) => setArticles(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const filtered = useMemo(() => {
    let list = [...articles];
    if (category !== "all") list = list.filter((a) => a.category === category);
    if (importance !== "all") list = list.filter((a) => a.importance === importance);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (a) =>
          a.title?.toLowerCase().includes(needle) ||
          a.summary?.toLowerCase().includes(needle) ||
          a.analysis?.toLowerCase().includes(needle) ||
          a.companies_concerned?.toLowerCase().includes(needle)
      );
    }
    list.sort((a, b) => {
      const da = a.published_at || "";
      const db = b.published_at || "";
      return sort === "recent" ? db.localeCompare(da) : da.localeCompare(db);
    });
    return list;
  }, [articles, category, importance, q, sort]);

  const hasFilters = q || category !== "all" || importance !== "all" || sort !== "recent";
  const reset = () => {
    setQ("");
    setCategory("all");
    setImportance("all");
    setSort("recent");
  };

  return (
    <>
      <Seo
        title="Veille & Décryptages — BENZENATI"
        description="Décryptages publiés pour les dirigeants de PME : intelligence artificielle, réglementations et données, cybersécurité, organisation et pilotage. Recherche et filtres par catégorie et importance."
        path="/veille"
      />
      <PageHero
        label="Veille & Décryptages"
        title="Des évolutions utiles, mises en perspective"
        intro="Une sélection de décryptages conçus pour aider les dirigeants de PME à comprendre les transformations en cours, sans bruit inutile."
        testid="veille-hero"
      />

      {/* Catégories */}
      <section className="border-b border-bz-line bg-bz-ivory2" data-testid="veille-categories">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-16">
          <span className="bz-label text-bz-petrol">Quatre champs d'observation</span>
          <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2">
            {CATEGORIES.map((c) => {
              const active = category === c.slug;
              return (
                <button
                  key={c.slug}
                  onClick={() => setCategory(active ? "all" : c.slug)}
                  data-testid={`category-tile-${c.slug}`}
                  aria-pressed={active}
                  className={`flex items-center justify-between gap-4 border px-6 py-5 text-left transition-colors duration-300 ${
                    active ? "border-bz-navy bg-bz-navy text-bz-ivory" : "border-bz-line bg-white text-bz-navy hover:border-bz-petrol"
                  }`}
                >
                  <span className="font-serif text-lg leading-snug">{c.label}</span>
                  <ArrowRight size={18} strokeWidth={1.5} className="shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16">
        <div className="flex flex-col gap-6 border-b border-bz-line pb-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search size={18} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-bz-slate" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un décryptage…"
              data-testid="veille-search"
              className="w-full border border-bz-line bg-bz-ivory py-3 pl-11 pr-4 text-sm text-bz-navy outline-none focus:border-bz-petrol"
            />
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="bz-label text-bz-slate">Importance</span>
              <select value={importance} onChange={(e) => setImportance(e.target.value)} data-testid="veille-importance"
                className="mt-2 border border-bz-line bg-bz-ivory px-3 py-2.5 text-sm text-bz-navy outline-none focus:border-bz-petrol">
                <option value="all">Toutes</option>
                {IMPORTANCE.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="bz-label text-bz-slate">Tri</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="veille-sort"
                className="mt-2 border border-bz-line bg-bz-ivory px-3 py-2.5 text-sm text-bz-navy outline-none focus:border-bz-petrol">
                <option value="recent">Plus récent</option>
                <option value="oldest">Plus ancien</option>
              </select>
            </label>
            {hasFilters && (
              <button onClick={reset} data-testid="veille-reset" className="inline-flex items-center gap-1.5 border border-bz-line px-4 py-2.5 text-xs uppercase tracking-[0.12em] text-bz-slate transition-colors hover:border-bz-navy hover:text-bz-navy">
                <X size={14} strokeWidth={1.8} /> Réinitialiser
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <p className="mt-14 text-bz-slate" data-testid="veille-loading">Chargement…</p>
        ) : error ? (
          <div className="mt-16 text-center" data-testid="veille-error">
            <p className="font-serif text-2xl text-bz-navy">Les décryptages n'ont pas pu être chargés.</p>
            <p className="mt-3 text-bz-slate">Une erreur technique est survenue. Il s'agit d'un problème de chargement, pas d'une absence de décryptage. Veuillez réessayer.</p>
            <button
              onClick={loadArticles}
              data-testid="veille-retry"
              className="mt-8 inline-flex border border-bz-navy px-6 py-3 text-xs uppercase tracking-[0.15em] text-bz-navy transition-colors hover:bg-bz-navy hover:text-bz-ivory"
            >
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center" data-testid="veille-empty">
            <p className="font-serif text-2xl text-bz-navy">Aucun décryptage ne correspond à votre recherche.</p>
            <p className="mt-3 text-bz-slate">Essayez d'élargir vos critères ou réinitialisez les filtres.</p>
            {hasFilters && (
              <button onClick={reset} className="mt-8 inline-flex border border-bz-navy px-6 py-3 text-xs uppercase tracking-[0.15em] text-bz-navy transition-colors hover:bg-bz-navy hover:text-bz-ivory">
                Réinitialiser les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/veille/${a.slug}`}
                  data-testid={`veille-article-${a.slug}`}
                  className="group flex h-full flex-col border border-bz-line bg-white transition-colors duration-300 hover:border-bz-petrol"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={a.image || "/articles/placeholder.webp"}
                      alt={a.image_alt || "Illustration éditoriale BENZENATI"}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="border border-bz-line px-3 py-1 text-[0.6rem] uppercase tracking-[0.12em] text-bz-slate">{categoryShort(a.category)}</span>
                      <span className={`border px-3 py-1 text-[0.6rem] uppercase tracking-[0.12em] ${importanceClass(a.importance)}`}>{importanceLabel(a.importance)}</span>
                    </div>
                    <p className="mt-4 text-xs text-bz-slate/70">{formatDate(a.published_at)}</p>
                    <h3 className="mt-2 font-serif text-2xl leading-snug text-bz-navy">{a.title}</h3>
                    <p className="mt-4 flex-1 text-base leading-relaxed text-bz-slate">{a.summary}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm text-bz-navy">
                      Lire le décryptage
                      <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
